import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Box } from '@mui/material';
import StatusChip from '../../Shared/StatusChip';
import {
  Status,
  SurveyType,
  WellnessResponse,
} from '../../../shared/constants';
import {
  isSurveyAttentionResponse,
  surveyScoreToLabel,
} from '../../../shared/surveys';
import {
  faBarsFilter,
  faFaceFrown,
  faFaceFrownSlight,
} from '@fortawesome/pro-solid-svg-icons';
import DashboardTableLegend from './DashboardTableLegend';
import CustomerCareDTable from '../../Shared/CustomerCareDTable';
import NoDataCard from './NoDataCard';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import ContactActionCell from './ContactActionCell';
import { fetchHubOwners } from '../../../store/hubOwner/actionCreators';
import { parseDateToLocal } from '../../../shared/utils';
import { customError, orange, warningBg } from '../../../mainTheme';

interface WellnessDetailPanelProps {
  selectedTab: number;
  isFacilityAdmin: boolean;
}

// Mirrors PatientSurveyDetailPanel: list the patients whose latest daily
// wellness check needs attention (SURVEY_ATTENTION_RESPONSES: Very bad / Bad),
// with the same columns. This map only styles those responses.
const ATTENTION_RESPONSES: Record<
  string,
  { color: string; icon: typeof faFaceFrown }
> = {
  [WellnessResponse.VeryBad]: { color: customError, icon: faFaceFrown },
  [WellnessResponse.Bad]: { color: orange, icon: faFaceFrownSlight },
};

const WellnessDetailPanel: React.FC<WellnessDetailPanelProps> = ({
  selectedTab,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();

  const { hubOwners, isLoading } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  useEffect(() => {
    if (!hubOwners?.length) {
      dispatch(fetchHubOwners());
    }
  }, [dispatch, hubOwners?.length]);

  const columns = [
    {
      field: 'patient',
      headerName: 'Patient',
      flex: 1,
    },
    ...(selectedTab !== 2
      ? [
          {
            field: 'facility_name',
            headerName: isFacilityAdmin ? 'Facility location' : 'Facility',
            flex: 1,
          },
        ]
      : []),
    {
      field: 'wellness_response',
      headerName: 'Wellness response',
      flex: 1,
      sortComparator: (v1: any, v2: any) => {
        return v1.text.localeCompare(v2.text);
      },
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center">
          <StatusChip
            icon={params.value.icon}
            iconColor={params.value.color}
            labelText={params.value.text}
            conditionalValue={params.value.text === Status.Unassigned}
            backgroundColor={warningBg}
          />
        </Box>
      ),
    },
    { field: 'hub_serial_number', headerName: 'Hub serial number', flex: 1 },
    { field: 'last_seen_online', headerName: 'Last online', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params: any) => (
        <ContactActionCell
          contactName={params.row.contact_name}
          contactPhone={params.row.contact_phone}
          contactEmail={params.row.contact_email}
        />
      ),
    },
  ];

  const rows: any[] = useMemo(() => {
    // GET /owners may send the raw 1-5 score or its label.
    let filteredHubOwners = (hubOwners || []).filter((owner) =>
      isSurveyAttentionResponse(
        SurveyType.HubDailyWellnessCheck,
        owner.last_wellness_score,
      ),
    );

    if (selectedTab === 1) {
      filteredHubOwners = filteredHubOwners.filter(
        (owner) => owner.facility_id != null,
      );
    } else if (selectedTab === 2) {
      filteredHubOwners = filteredHubOwners.filter(
        (owner) => owner.facility_id == null,
      );
    }

    return filteredHubOwners.map((owner) => {
      const hasPatient = owner?.first_name || owner?.last_name;
      const label = surveyScoreToLabel(
        SurveyType.HubDailyWellnessCheck,
        owner.last_wellness_score,
      ) as string;
      const response = ATTENTION_RESPONSES[label];

      // Adapt action values based on Role
      const contactName = isFacilityAdmin
        ? `${owner?.first_name ?? ''} ${owner?.last_name ?? ''}`.trim()
        : owner.contact_name || null;
      const contactPhone = isFacilityAdmin
        ? owner.phone || null
        : owner.contact_phone || null;
      const contactEmail = isFacilityAdmin
        ? owner.email || null
        : owner.contact_email || null;

      return {
        id: owner.customer_id,
        patient: hasPatient
          ? `${owner?.first_name} ${owner?.last_name}`
          : 'N/A',
        facility_name: owner.facility_name || 'N/A',
        wellness_response: {
          icon: response.icon,
          color: response.color,
          text: label,
        },
        hub_serial_number: owner?.serial_number || 'N/A',
        last_seen_online: owner.last_seen_online
          ? parseDateToLocal(owner.last_seen_online, 'yyyy-MM-dd HH:mm:ss')
          : 'N/A',
        action: faBarsFilter,
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
      };
    });
  }, [hubOwners, selectedTab, isFacilityAdmin]);

  const tableLegendData = [
    {
      name: WellnessResponse.VeryBad,
      color: customError,
    },
    {
      name: WellnessResponse.Bad,
      color: orange,
    },
  ];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <DashboardTableLegend tableLegendData={tableLegendData} />
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSize={5}
            defaultSortField="wellness_response"
            defaultSortDirection="asc"
            hideViewAllButton
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default WellnessDetailPanel;
